/* eslint-disable react-refresh/only-export-components -- context module: provider + hook together */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * The tenant a platform operator is currently accessing, under which case
 * reference. Every per-tenant platform request carries this as
 * `X-Case-Reference` (Privileged Access Service, API Spec §3.4). Kept in
 * sessionStorage so it survives navigation within the console but not a new
 * tab / browser restart — cross-tenant access should be a deliberate act
 * each session (Architecture §5.3).
 */
export interface TenantAccess {
  tenantId: string;
  caseReference: string;
}

interface TenantAccessContextValue {
  access: TenantAccess | null;
  open: (access: TenantAccess) => void;
  clear: () => void;
}

const KEY = 'mw.platform.access';
const TenantAccessContext = createContext<TenantAccessContextValue | null>(null);

function read(): TenantAccess | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as TenantAccess;
    return v.tenantId && v.caseReference ? v : null;
  } catch {
    return null;
  }
}

export function TenantAccessProvider({ children }: { children: ReactNode }) {
  const [access, setAccess] = useState<TenantAccess | null>(read);

  const open = useCallback((next: TenantAccess) => {
    try {
      window.sessionStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* private mode — context still works for this session */
    }
    setAccess(next);
  }, []);

  const clear = useCallback(() => {
    try {
      window.sessionStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
    setAccess(null);
  }, []);

  const value = useMemo(() => ({ access, open, clear }), [access, open, clear]);
  return <TenantAccessContext.Provider value={value}>{children}</TenantAccessContext.Provider>;
}

export function useTenantAccess(): TenantAccessContextValue {
  const ctx = useContext(TenantAccessContext);
  if (!ctx) throw new Error('useTenantAccess must be used within <TenantAccessProvider>');
  return ctx;
}
